import { observable, computed, toJS, action } from 'mobx';

import { SerializedData, Store, AnalyticsStore } from 'bernie-core';

import { InputValidator } from 'stores/validators';
import { SearchCriteriaStore } from '.';
import {
  trackAnalytics,
  flightsMultiPluralLocalizationTemplate,
  formatDateToIsoString,
  setDateField,
  trackSearchFormAnalytics,
  getSearchFormTriggerLocationLabel
} from 'utility';
import {
  FlightsTripType,
  FlightsJourneyCriteriaInput,
  FlightsTravelerDetailsInput,
  FlightsShoppingPwaFlightSearchResultsFlightsSearch
} from '__generated__/api/types';
import {
  FlightsSearchFormType,
  FlightsSearchFormJourneyCriteriaType,
  FlightsSearchFormLocationFieldType,
  FlightsSearchFormError,
  FlightsSearchFormDateFieldType,
  FlightsSearchFormTravelersOption,
  TravelerOptionItem,
  FlightsSearchFormAirlineOption,
  FlightsSearchFormCabinClassOption,
  FlightsSearchFormTripTypeOption,
  FlightsSearchFormRemoveJourneyActionType,
  FlightsSearchFormTripTypeOptionItem
} from 'types/search/wizard';
import { FlightsSimpleLocalizationTemplate } from 'src/types/shared';
import { FlightsLocation } from 'utility/flights-locations';

export class SearchFormStore extends Store {
  @observable public searchForm: FlightsSearchFormType;
  @observable public essEndpoint: string;
  @observable public loading: boolean;
  @observable public journeyIds: number[];
  @observable public firstErrorRegistered = false;
  @observable public firstErrorRef;
  @observable public userStartedLocationTyping = false;

  public MAX_NUMBER_JOURNEYS: number;

  public searchFormInitialState: SerializedData;

  public initialTravelerOptions: TravelerOptionItem[];
  public focusErrorSummaryInTravelers: boolean;
  public shouldFocusLastAddedTrip: boolean;

  public hydrate(data: SerializedData): void {
    Object.assign(this, data);
  }

  @action
  public setUserStartedLocationTyping = (value: boolean) => {
    this.userStartedLocationTyping = value;
  };

  public setFirstErrorRef = (ref): void => {
    this.firstErrorRef = ref;
    this.firstErrorRegistered = true;
  };

  public clearErrorRef = () => {
    this.firstErrorRef = null;
    this.firstErrorRegistered = false;
  };

  public addNewBlankJourney = (): void => {
    const searchForm = this.searchForm;

    if (searchForm.journeys.length < this.MAX_NUMBER_JOURNEYS) {
      const lastJourney = searchForm.journeys[searchForm.journeys.length - 1];

      const newJourney = toJS(searchForm.newJourneyTemplate) as FlightsSearchFormJourneyCriteriaType;

      newJourney.originLocation.value = '';
      newJourney.destinationLocation.value = '';
      newJourney.departureDate.date = {};
      newJourney.departureDate.currentError = '';
      newJourney.label = toJS(lastJourney.label) as FlightsSimpleLocalizationTemplate;
      searchForm.journeys.push(newJourney);
    }
  };

  public removeJouney = (
    journeyIndex: number,
    callback?: (removeJourneyAction: FlightsSearchFormRemoveJourneyActionType) => void
  ) => {
    const { journeyIds, searchForm } = this;
    journeyIds.splice(journeyIndex, 1);
    const lastId = journeyIds[journeyIds.length - 1];
    journeyIds.push(lastId + 1);

    searchForm.journeys = searchForm.journeys.filter((journey, index) => {
      const { removeJourneyAction } = journey;

      if (index === journeyIndex && removeJourneyAction) {
        callback(removeJourneyAction);
      }

      return index !== journeyIndex;
    });
  };

  public cleanUpInitialTravelersOptions() {
    this.initialTravelerOptions = [];
  }

  public setJourneyDates = (journeyIndex: number, selectedDepartureDate: Date, selectedReturnDate: Date) => {
    const { searchForm } = this;
    const journey = searchForm.journeys[journeyIndex];

    setDateField(journey.departureDate, selectedDepartureDate);

    if (selectedReturnDate) {
      setDateField(journey.returnDate, selectedReturnDate);
    } else if (journey.returnDate && journey.returnDate.date) {
      journey.returnDate.date = null;
    }
  };

  public setLocation = (journeyIndex: number, location: FlightsLocation, selectionTerm: string) => {
    const { searchForm } = this;
    const journey = searchForm.journeys[journeyIndex];

    if (location === FlightsLocation.DESTINATION) {
      return (journey.destinationLocation.value = selectionTerm);
    }

    journey.originLocation.value = selectionTerm;
  };

  public restoreSearchFormToInitialState(): void {
    Object.assign(this, this.searchFormInitialState);
  }

  public setSearchFormInitialState(): void {
    this.searchFormInitialState = this.toJSON();
    this.searchFormInitialState['loading'] = undefined;
    this.searchFormInitialState['searchFormInitialState'] = undefined;
  }

  public setSelectedTripType(selectedTripType: string, key: string): void {
    const { searchForm } = this;
    const updatedSearchForm = toJS(searchForm);
    const tripTypeOptions = updatedSearchForm.searchOptions.find(
      item => item.__typename === 'FlightsSearchFormTripTypeOption'
    ) as FlightsSearchFormTripTypeOption;

    tripTypeOptions.items.forEach((item: FlightsSearchFormTripTypeOptionItem) => {
      item.selected = item[key] === selectedTripType;
    });

    this.searchForm = updatedSearchForm;
    this.setJourneysForSelectedTripType();
    this.clearErrorRef();
  }

  public setJourneysForSelectedTripType(): void {
    const { searchForm, selectedTripTypeOptionItem } = this;

    if (selectedTripTypeOptionItem.tripType !== FlightsTripType.ROUND_TRIP) {
      const { journeys } = searchForm;
      const journey = journeys[0];
      journey.returnDate.date = null;
      journey.returnDate.currentError = null;
    }

    if (selectedTripTypeOptionItem.tripType !== FlightsTripType.MULTI_DESTINATION && searchForm.journeys.length > 1) {
      searchForm.journeys = searchForm.journeys.slice(0, 1);
    }

    if (selectedTripTypeOptionItem.tripType === FlightsTripType.MULTI_DESTINATION && searchForm.journeys.length === 1) {
      this.addNewBlankJourney();
    }
  }

  public setLoading(variables: FlightsShoppingPwaFlightSearchResultsFlightsSearch.Variables): void {
    // if previous search's od pair and dates are different from new
    this.loading = !(variables.searchFilterValuesList && variables.searchFilterValuesList.length > 0);
  }

  public setInitialTravelerOptions() {
    const travelerOptions = this.getSearchOption(
      'FlightsSearchFormTravelersOption'
    ) as FlightsSearchFormTravelersOption;

    if (this.initialTravelerOptions.length === 0) {
      this.initialTravelerOptions = toJS(travelerOptions.items);
    }
  }

  public syncWithSearchCriteriaStore(searchCriteriaStore: SearchCriteriaStore): void {
    const searchFormFeedback = Object.assign({}, this.searchForm);

    Object.assign(searchFormFeedback, this.getJourneysFromSearchCriteria(searchCriteriaStore));
    Object.assign(searchFormFeedback, this.getTravelersFromSearchCriteria(searchCriteriaStore));
    Object.assign(searchFormFeedback, this.getAirlineFromSearchCriteria(searchCriteriaStore));
    Object.assign(searchFormFeedback, this.getCabinClassFromSearchCriteria(searchCriteriaStore));

    this.searchForm = searchFormFeedback;
  }

  public syncJourneysWithSearchCriteriaStore(searchCriteriaStore: SearchCriteriaStore) {
    const searchFormFeedback = Object.assign({}, this.searchForm);

    Object.assign(searchFormFeedback, this.getJourneysFromSearchCriteria(searchCriteriaStore));

    this.searchForm = searchFormFeedback;
  }

  private getJourneysFromSearchCriteria({ searchCriteria }: SearchCriteriaStore) {
    searchCriteria.journeyCriteria.forEach((journeyCriteria: FlightsJourneyCriteriaInput, index: number) => {
      let journey = this.searchForm.journeys[index];

      if (searchCriteria.flightsSearchContext.tripType === FlightsTripType.ROUND_TRIP && index !== 0) {
        const previousJourney = this.searchForm.journeys[index - 1];

        previousJourney.returnDate.date = {
          isoFormat: formatDateToIsoString(journeyCriteria.departureDate),
          ...journeyCriteria.departureDate
        };

        return;
      }

      if (!journey) {
        this.searchForm.journeys.push(toJS(this.searchForm.newJourneyTemplate));
        journey = this.searchForm.journeys[index];

        journey.originLocation.value = '';
        journey.destinationLocation.value = '';
        journey.departureDate.date = {};
        journey.departureDate.currentError = '';
      }

      journey.originLocation.value = journeyCriteria.origin;
      journey.destinationLocation.value = journeyCriteria.destination;
      journey.departureDate.date = {
        isoFormat: formatDateToIsoString(journeyCriteria.departureDate),
        ...journeyCriteria.departureDate
      };
    });
  }

  private getTravelersFromSearchCriteria({ searchCriteria }: SearchCriteriaStore) {
    searchCriteria.travelerDetails.forEach((travelerDetails: FlightsTravelerDetailsInput, index: number) => {
      const travelerItems = (this.searchForm.searchOptions.find(
        searchOption => searchOption.__typename === 'FlightsSearchFormTravelersOption'
      ) as FlightsSearchFormTravelersOption).items;

      travelerItems[index].travelerCount = travelerDetails.count;
    });
  }

  private getAirlineFromSearchCriteria({ searchCriteria }: SearchCriteriaStore) {
    const preferedAirline = searchCriteria.searchPreferences.airline;
    const airlineOption = this.searchForm.searchOptions.find(
      searchOption => searchOption.__typename === 'FlightsSearchFormAirlineOption'
    ) as FlightsSearchFormAirlineOption;
    const { selectedItem, items } = airlineOption;

    return selectedItem.airline === preferedAirline
      ? (airlineOption.selectedItem = items.find(airline => airline.airline === preferedAirline))
      : items[0];
  }

  private getCabinClassFromSearchCriteria({ searchCriteria }: SearchCriteriaStore) {
    const criteriaClass = searchCriteria.searchPreferences.cabinClass;
    const cabinClassOption = this.searchForm.searchOptions.find(
      searchOption => searchOption.__typename === 'FlightsSearchFormCabinClassOption'
    ) as FlightsSearchFormCabinClassOption;

    return (cabinClassOption.selectedItem = cabinClassOption.items.find(
      cabinClass => cabinClass.cabinClass === criteriaClass
    ));
  }

  public getSearchOption(optionType: string) {
    return this.searchForm.searchOptions.find(option => option.__typename === optionType);
  }

  private resetSearchFormErrors(searchForm: FlightsSearchFormType): void {
    searchForm.journeys.forEach(journey => {
      const { destinationLocation, originLocation, departureDate, returnDate } = journey;

      destinationLocation.currentError = null;
      originLocation.currentError = null;
      departureDate.currentError = null;

      if (returnDate) {
        returnDate.currentError = null;
      }
    });
    const travelerOption = searchForm.searchOptions.find(
      option => option.__typename === 'FlightsSearchFormTravelersOption'
    ) as FlightsSearchFormTravelersOption;
    travelerOption.currentErrors = null;
  }

  public revertToInitialTravelerOptions() {
    const travelerOptions = this.getSearchOption(
      'FlightsSearchFormTravelersOption'
    ) as FlightsSearchFormTravelersOption;

    travelerOptions.items = this.initialTravelerOptions;
    this.cleanUpInitialTravelersOptions();
    travelerOptions.currentErrors = [];
  }

  public validateAndRestrict(searchForm: FlightsSearchFormType): void {
    const { journeys, searchOptions } = searchForm;
    const travelerOption = searchOptions.find(
      option => option.__typename === 'FlightsSearchFormTravelersOption'
    ) as FlightsSearchFormTravelersOption;

    travelerOption.items.forEach(item => {
      const { minCount, max } = item;
      if (item.travelerCount > max) {
        item.travelerCount = item.max;
        if (item.travelerAges) {
          item.travelerAges = item.travelerAges.splice(0, max);
        }
      } else if (item.travelerCount < minCount) {
        item.travelerCount = minCount;
        if (item.travelerAges) {
          item.travelerAges = [];
        }
      }
    });

    journeys.forEach(journey => {
      const todayDate = new Date();
      const isoDateForDatePicker = todayDate.toISOString().split('T')[0];

      if (journey.departureDate && journey.departureDate.currentError) {
        journey.departureDate.date.isoFormat = isoDateForDatePicker;
      }

      if (journey.returnDate && journey.returnDate.currentError) {
        journey.returnDate.date.isoFormat = isoDateForDatePicker;
      }
    });
  }

  public validate(analyticsStore?: AnalyticsStore): boolean {
    let valid = true;
    this.resetSearchFormErrors(this.searchForm);

    valid = this.validateJourneys(this.searchForm.journeys, analyticsStore) && valid;
    valid = this.areTravelerInputsValid(analyticsStore) && valid;

    return valid;
  }

  public areTravelerInputsValid(analyticsStore: AnalyticsStore) {
    const searchOptions = this.searchForm.searchOptions;
    let valid = true;

    const travelerOption = searchOptions.find(
      option => option.__typename === 'FlightsSearchFormTravelersOption'
    ) as FlightsSearchFormTravelersOption;

    travelerOption.possibleErrors.every(error => {
      const validator = InputValidator.TRAVELER[error.type] as (traveler: FlightsSearchFormTravelersOption) => boolean;
      const passValidation = validator ? validator(travelerOption) : true;
      const currentErrors = travelerOption.currentErrors || [];

      if (!passValidation) {
        travelerOption.currentErrors = [...currentErrors, error.message];
        trackAnalytics(error.analytics, analyticsStore);
        valid = false;
      }

      return valid;
    });

    return valid;
  }

  private validateJourneys(journeys: FlightsSearchFormJourneyCriteriaType[], analyticsStore: AnalyticsStore): boolean {
    let valid = true;

    journeys.forEach((journey, index) => {
      const { destinationLocation, originLocation, departureDate, returnDate } = journey;
      const previousJourneyDepartureDate = index >= 1 ? journeys[index - 1].departureDate : null;

      valid = this.validateLocations(destinationLocation, originLocation, analyticsStore, index) && valid;
      valid =
        this.validateDates(departureDate, returnDate, previousJourneyDepartureDate, analyticsStore, index) && valid;
    });

    return valid;
  }

  private validateLocations(
    destinationLocation: FlightsSearchFormLocationFieldType,
    originLocation: FlightsSearchFormLocationFieldType,
    analyticsStore: AnalyticsStore,
    journeyIndex: number
  ): boolean {
    let valid = true;

    valid = this.validateLocation(originLocation, destinationLocation, analyticsStore, journeyIndex) && valid;
    valid = this.validateLocation(destinationLocation, originLocation, analyticsStore, journeyIndex) && valid;

    return valid;
  }

  private validateLocation(
    locationA: FlightsSearchFormLocationFieldType,
    locationB: FlightsSearchFormLocationFieldType,
    analyticsStore: AnalyticsStore,
    journeyIndex: number
  ): boolean {
    let valid = true;
    let locationError: FlightsSearchFormError;

    locationA.possibleErrors.forEach(error => {
      const { type } = error;
      const validator = InputValidator.LOCATION[type] as (locationA: string, locationB?: string) => boolean;
      const passValidation = validator ? validator(locationA.value, locationB.value) : true;

      if (!passValidation) {
        locationError = error;
        valid = false;
      }
    });

    if (locationError) {
      const { message, analytics } = locationError;

      locationA.currentError = message;
      trackSearchFormAnalytics(analyticsStore, analytics, this.selectedTripTypeOptionItem.tripType, journeyIndex + 1);
    }

    return valid;
  }

  private validateDates(
    departureDate: FlightsSearchFormDateFieldType,
    returnDate: FlightsSearchFormDateFieldType,
    previousJourneyDepartureDate: FlightsSearchFormDateFieldType,
    analyticsStore: AnalyticsStore,
    journeyIndex: number
  ): boolean {
    let valid = true;
    const { tripType } = this.selectedTripTypeOptionItem;

    valid = this.validateDate(departureDate, previousJourneyDepartureDate, analyticsStore, journeyIndex) && valid;

    if (tripType === FlightsTripType.ROUND_TRIP) {
      valid = this.validateDate(returnDate, previousJourneyDepartureDate, analyticsStore, journeyIndex) && valid;
    }

    return valid;
  }

  private validateDate(
    dateA: FlightsSearchFormDateFieldType,
    dateB: FlightsSearchFormDateFieldType,
    analyticsStore: AnalyticsStore,
    journeyIndex: number
  ): boolean {
    let valid = true;

    dateA.possibleErrors.every(error => {
      const validator = InputValidator.DATE[error.type] as (
        dateA: FlightsSearchFormDateFieldType,
        dateB?: FlightsSearchFormDateFieldType
      ) => boolean;
      const passValidation = validator ? validator(dateA, dateB) : true;

      if (!passValidation) {
        dateA.currentError = error.message;
        trackSearchFormAnalytics(
          analyticsStore,
          error.analytics,
          this.selectedTripTypeOptionItem.tripType,
          journeyIndex + 1
        );
        valid = false;
      }

      return valid;
    });

    return valid;
  }

  @computed get travelersLabel() {
    let travelersLabel = '';
    this.searchForm.searchOptions.map(option => {
      if (option.__typename === 'FlightsSearchFormTravelersOption') {
        let travelersCount = 0;
        const replacerStr = '{numberOfTravelers}';
        option.items.forEach((item: TravelerOptionItem) => (travelersCount += item.travelerCount));

        travelersLabel = flightsMultiPluralLocalizationTemplate(travelersCount, option.valueTemplate, replacerStr);
      }
    });

    return travelersLabel;
  }

  @computed get selectedTripTypeOptionItem(): FlightsSearchFormTripTypeOptionItem {
    return this.tripTypeOptions.items.find(item => item.selected);
  }

  @computed get tripTypeOptions(): FlightsSearchFormTripTypeOption {
    return this.searchForm.searchOptions.find(
      item => item.__typename === 'FlightsSearchFormTripTypeOption'
    ) as FlightsSearchFormTripTypeOption;
  }

  @computed get shouldRenderErrorSummary() {
    let errors = 0;

    this.searchForm.journeys.forEach(journey => {
      if (journey.originLocation.currentError) {
        errors++;
      }

      if (journey.destinationLocation.currentError) {
        errors++;
      }

      if (journey.departureDate.currentError) {
        errors++;
      }

      if (journey.returnDate && journey.returnDate.currentError) {
        errors++;
      }
    });

    return errors > 1;
  }

  @computed get hasTravelerErrors() {
    const travelerOptions = this.searchForm.searchOptions.find(
      option => option.__typename === 'FlightsSearchFormTravelersOption'
    ) as FlightsSearchFormTravelersOption;

    return travelerOptions.currentErrors && travelerOptions.currentErrors.length > 0;
  }

  @computed get currentOriginAndDestinationLocationsLabel(): string {
    const { searchForm, loading, searchFormInitialState } = this;
    const originAndDestinationLocations = searchForm && searchForm.originAndDestinationLocations;

    if (searchForm && searchForm.journeys) {
      return getSearchFormTriggerLocationLabel(
        originAndDestinationLocations,
        searchForm.journeys[0],
        searchFormInitialState,
        loading
      );
    }

    return '';
  }
}

SearchFormStore.DEFAULTS = {
  loading: false,
  journeyIds: [1, 2, 3, 4, 5],
  MAX_NUMBER_JOURNEYS: 5,
  initialTravelerOptions: [],
  focusErrorSummaryInTravelers: false,
  shouldFocusLastAddedTrip: false
};
